import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'
import { useUiStore } from '@/stores/ui'
import { useAuthService } from '@/services/auth/authService'
import { Routes } from '@/router/routes'
import { RouterContexts } from '@/router/RouterContexts'
import { useUi } from '@/composables/useUi'
import { useHelpers } from '@/composables/useHelpers'

import type { NavigationGuardNext, RouteLocationNormalized, Router } from 'vue-router'
import type { AppPermissions } from '@/types/enums/permissions/permissions'
import { Middleware } from '@/types/enums/middleware/middleware'

export function useMiddleware(router: Router) {
  const { formatDate } = useHelpers()
  /**
   * If the route requires authentication, check if the user is authenticated. If not,
   * redirect to login page.
   */
  const authenticateNavigation = async (
    to: RouteLocationNormalized,
    next: NavigationGuardNext,
  ) => {
    const {
      storeAuthenticatedUser,
    } = storeToRefs(useUserStore())
    const {
      fetchAndSetStoreAuthenticatedUser,
    } = useAuthService()

    const requiresAuth = to.matched.some((record) => {
      // @ts-expect-error - middleware is not defined on RouteRecordRaw
      return record.meta?.middleware?.includes(Middleware.REQUIRES_AUTH)
    })

    /**
     * Route requires authentication, but user is not authenticated.
     */
    if (requiresAuth && !storeAuthenticatedUser.value) {
      await fetchAndSetStoreAuthenticatedUser()
      return storeAuthenticatedUser.value ? next() : next(Routes.Login)
      /**
       * Authenticated user is trying to access login page. Redirect to
       * dashboard.
       */
    }
    else if (to.name === Routes.Login && storeAuthenticatedUser.value) {
      await router.push({ name: Routes.Dashboard })
      /**
       * Route doesn't require authentication. Continue to next route.
       */
    }
    else {
      return next()
    }
  }

  const checkRouterContext = (
    to: RouteLocationNormalized,
    next: NavigationGuardNext,
  ) => {
    const {
      storeAuthenticatedUser,
    } = storeToRefs(useUserStore())

    const routeIsPortalContext = to.matched.some((record) => {
      // @ts-expect-error - routerContexts is not defined on RouteRecordRaw
      return record.meta?.routerContexts?.includes(RouterContexts.PORTAL_CONTEXT)
    })

    const routeIsDailyAgendaContext = to.matched.some((record) => {
      // @ts-expect-error - routerContexts is not defined on RouteRecordRaw
      return record.meta?.routerContexts?.includes(RouterContexts.DAILY_AGENDA_CONTEXT)
    })

    const userIsAllir = storeAuthenticatedUser.value?.email === 'allir@extreme.is'

    if (routeIsPortalContext && !userIsAllir) {
      return next()
    }

    if (routeIsDailyAgendaContext && userIsAllir) {
      return next()
    }

    return next({ name: Routes.Forbidden })
  }

  const checkUserHasPermissions = (
    to: RouteLocationNormalized,
    next: NavigationGuardNext,
  ) => {
    const {
      storeAuthenticatedUser,
    } = storeToRefs(useUserStore())

    const requiredPermissions = to.matched
      .map(record => record.meta.permission)
      .filter(Boolean) as AppPermissions[]

    const anyPermissionGroups = to.matched
      .map(record => record.meta.anyPermissions)
      .filter((permissions): permissions is AppPermissions[] => Array.isArray(permissions))

    if (requiredPermissions.length === 0 && anyPermissionGroups.length === 0) {
      return next()
    }

    if (storeAuthenticatedUser.value === null) {
      return next({ name: Routes.Forbidden })
    }

    const { authenticatedUserHasPermission } = useAuthService()
    const userHasRequiredPermissions = requiredPermissions.every(permission =>
      authenticatedUserHasPermission(permission),
    )
    const userHasAnyPermissionGroups = anyPermissionGroups.every(permissions =>
      permissions.some(permission => authenticatedUserHasPermission(permission)),
    )

    return userHasRequiredPermissions && userHasAnyPermissionGroups
      ? next()
      : next({ name: Routes.Forbidden })
  }

  /**
   * If the user is on a mobile device (or screen size < 768), and they click on a
   * link with the "togglesNav" middleware applied, we close the nav drawer. We don't
   * want this to happen on desktop, as it's large enough to have the nav drawer open
   * and still see the content.
   */
  const toggleNavDrawer = (to: RouteLocationNormalized, next: NavigationGuardNext) => {
    const thisRouteTogglesNav = to.matched.some((record) => {
      // @ts-expect-error - middleware is not defined on RouteRecordRaw
      return record.meta?.middleware?.includes(Middleware.TOGGLES_NAV_DRAWER)
    })
    if (thisRouteTogglesNav) {
      const { storeNavDrawerIsOpen } = storeToRefs(useUiStore())
      const {
        storeToggleNavDrawerIsOpen,
      } = useUiStore()
      if (storeNavDrawerIsOpen.value && window.innerWidth < 768) {
        storeToggleNavDrawerIsOpen()
      }
    }
    return next()
  }

  /**
   * In some cases (like the staff schedule) it's best to close the nav drawer regardless
   * of screen size. This middleware is used for those cases.
   */
  const forceToggleNavDrawer = (to: RouteLocationNormalized, next: NavigationGuardNext) => {
    const thisRouteTogglesNav = to.matched.some((record) => {
      // @ts-expect-error - middleware is not defined on RouteRecordRaw
      return record.meta?.middleware?.includes(Middleware.FORCE_TOGGLES_NAV_DRAWER)
    })
    if (thisRouteTogglesNav) {
      const {
        storeToggleNavDrawerIsOpen,
      } = useUiStore()
      const {
        storeNavDrawerIsOpen,
      } = storeToRefs(useUiStore())
      if (storeNavDrawerIsOpen.value) {
        storeToggleNavDrawerIsOpen()
      }
    }
    return next()
  }

  /**
   * This middleware forces the current date in the format YYYY-MM-DD to be appended to
   * the query params of the route. This is used in the staff schedule to ensure that
   * the correct month is displayed when the user navigates to the page.
   */
  const addCurrentDateQueryParam = (to: RouteLocationNormalized, next: NavigationGuardNext) => {
    const appendCurrentDate = to.matched.some((record) => {
      // @ts-expect-error - middleware is not defined on RouteRecordRaw
      return record.meta?.middleware?.includes(Middleware.ADD_CURRENT_DATE_QUERY_PARAM)
    })
    if (appendCurrentDate && !to.query.date) {
      const currentDate = new Date()
      const currentDay = currentDate.getDate()
      const currentMonth = currentDate.getMonth() + 1
      const currentYear = currentDate.getFullYear()
      const dateInView = formatDate(`${currentYear}-${currentMonth.toString().padStart(2, '0')}-${currentDay.toString().padStart(2, '0')}`)
      const { query } = to
      query.date = dateInView
      next({ path: to.path, query })
    }
    else {
      return next()
    }
  }

  /**
   * This middleware resets the UI between route changes. This is used to ensure that
   * the UI is in a consistent state when the user navigates to a new route.
   */
  const resetUiMiddleware = (next: NavigationGuardNext) => {
    const { uiActions } = useUi()
    const {
      storeResetUi,
      storeActionIsInProgress,
    } = useUiStore()
    if (!storeActionIsInProgress(uiActions.bootingApp)) {
      storeResetUi()
    }
    next()
  }

  /**
   * Removes the 'bootingApp' action from the actionsInProgress store. This hides the splash
   * screen.
   */
  const delayLoadingScreenRemoval = (next: NavigationGuardNext) => {
    const { uiActions } = useUi()
    const {
      storeActionIsInProgress,
      storeRemoveFromActionsInProgress,
    } = useUiStore()
    /**
     * Because the app will likely boot pretty quickly most of the time, the splash screen
     * will only be visible for a split second. This makes what should be a nice UX feature
     * actually a bit annoying. What we'll do here is add a 250ms delay before removing the
     * 'bootingApp' action from the actionsInProgress store. This essentially sets a minimum
     * time that the splash screen will be visible for.
     *
     * The 250ms delay applies AFTER the app has booted. So if the app takes 500ms to boot,
     * the splash screen will be visible for 750ms.
     */
    if (storeActionIsInProgress(uiActions.bootingApp)) {
      setTimeout(() => {
        storeRemoveFromActionsInProgress(uiActions.bootingApp)
      }, 250)
    }
    next()
  }

  const middlewareMethods = [
    async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => await authenticateNavigation(to, next),
    (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => checkRouterContext(to, next),
    (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => checkUserHasPermissions(to, next),
    (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => toggleNavDrawer(to, next),
    (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => forceToggleNavDrawer(to, next),
    (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => addCurrentDateQueryParam(to, next),
    (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => resetUiMiddleware(next),
    (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => delayLoadingScreenRemoval(next),
  ]

  return {
    Middleware,
    middlewareMethods,
  }
}
